import AnchorLink from "@bmi-digital/components/anchor-link";
import MasonryGrid from "@bmi-digital/components/masonry-grid";
import classnames from "classnames";
import React from "react";
import { NavigationHeading, NavigationSeparator } from "@bmi/payload-types";
import { ExternalLinkBlock, InternalLinkBlock } from "./link/types";
import { toAnchorLinkActionProps } from "./link/utils";
import {
  classes,
  StyledSitemapBlock,
  StyledSitemapItem
} from "./styles/SitemapBlock.styles";
import { useSiteContext } from "./Site";
import { FooterMainNavigation } from "./Footer";
import { NavigationMenu, NavigationRootMenu } from "./Header";

type Props = {
  label?: string;
  level?: number;
  isChild?: boolean;
};

const levelVariantMap = {
  max: 2,
  0: {
    label: "h4" as const,
    link: "h5" as const
  },
  1: {
    label: "h5" as const,
    link: "h6" as const
  },
  2: {
    label: "h6" as const,
    link: "h6" as const
  }
};

const WrapperComponent = ({
  isChild,
  children
}: {
  isChild: boolean;
  children: React.ReactNode;
}) => (
  <StyledSitemapBlock className={classnames(isChild && classes.child)}>
    {isChild ? <div>{children}</div> : <MasonryGrid>{children}</MasonryGrid>}
  </StyledSitemapBlock>
);

type InternalOrExternalLinkBlockSitemapItemProps = Props & {
  links: (InternalLinkBlock | ExternalLinkBlock)[];
};

const InternalOrExternalLinkBlockSitemapItem = ({
  level,
  link
}: {
  level: number;
  link: InternalLinkBlock | ExternalLinkBlock;
}) => {
  const { countryCode } = useSiteContext();
  return (
    <StyledSitemapItem
      variant={levelVariantMap[level.toString()].link}
      key={`${level}-${link.label}`}
    >
      <AnchorLink
        {...toAnchorLinkActionProps(link, countryCode)}
        data-testid={"sitemap-link"}
      >
        {link.label}
      </AnchorLink>
    </StyledSitemapItem>
  );
};

export const InternalOrExternalLinkBlockSitemapBlock = ({
  links,
  level = 0
}: InternalOrExternalLinkBlockSitemapItemProps) => {
  return (
    <WrapperComponent isChild={level > 0}>
      {links.map((link) => (
        <InternalOrExternalLinkBlockSitemapItem
          key={link.label}
          level={level}
          link={link}
        />
      ))}
    </WrapperComponent>
  );
};

const isChildSitemapItem = (
  link:
    | InternalLinkBlock
    | ExternalLinkBlock
    | NavigationHeading
    | NavigationSeparator
    | NavigationMenu
): link is InternalLinkBlock | ExternalLinkBlock | NavigationMenu => {
  return (
    link.blockType === "internal-link-block" ||
    link.blockType === "external-link-block" ||
    link.blockType === "navigation-menu"
  );
};

type MenuNavigationBlockSitemapProps = Props & {
  links: (
    | InternalLinkBlock
    | ExternalLinkBlock
    | NavigationRootMenu
    | NavigationMenu
  )[];
};

export const MenuNavigationBlockSitemap = ({
  links,
  label,
  level = 0
}: MenuNavigationBlockSitemapProps) => {
  return (
    <WrapperComponent isChild={level > 0}>
      {links.map((link) => {
        return link.blockType === "internal-link-block" ||
          link.blockType === "external-link-block" ? (
          <div key={`${level}-${label}`}>
            <InternalOrExternalLinkBlockSitemapItem level={level} link={link} />
          </div>
        ) : (
          <div key={`${level}-${label}`}>
            <StyledSitemapItem
              variant={levelVariantMap[level.toString()].label}
            >
              {link.label}
            </StyledSitemapItem>
            <MenuNavigationBlockSitemap
              level={
                level + 1 <= levelVariantMap.max
                  ? level + 1
                  : levelVariantMap.max
              }
              links={
                link.links.filter(isChildSitemapItem) as (
                  | InternalLinkBlock
                  | ExternalLinkBlock
                  | NavigationMenu
                )[]
              }
              isChild
            />
          </div>
        );
      })}
    </WrapperComponent>
  );
};

type FooterMainNavigationBlockSitemapProps = Props & {
  data: FooterMainNavigation[];
};

export const FooterMainNavigationBlockSitemap = ({
  data
}: FooterMainNavigationBlockSitemapProps) => {
  return (
    <WrapperComponent isChild={false}>
      {data.map((footerMainNavigation) => (
        <div key={`${footerMainNavigation.label}`}>
          <StyledSitemapItem variant={levelVariantMap["0"].label}>
            {footerMainNavigation.label}
          </StyledSitemapItem>
          <InternalOrExternalLinkBlockSitemapBlock
            level={1}
            links={footerMainNavigation.links}
            isChild
          />
        </div>
      ))}
    </WrapperComponent>
  );
};
